import { GoogleLogin } from '@react-oauth/google'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card/card'
import { Form, FormField, FormItem, FormLabel } from '@/components/form/form'
import { PasswordInput } from '@/components/input/password-input'
import { Separator } from '@/components/separator/separator'

import { useResizeWindow } from '@/lib/hooks/use-resize-window'
import { cn } from '@/lib/utils'

import { useAccountVerificationCard } from './use-account-verification-card'

export interface AccountVerificationCardProps {
  onSuccess: VoidFunction
}

export const AccountVerificationCard = ({ onSuccess }: AccountVerificationCardProps) => {
  const {
    form,
    formErrors,
    cardWrapperRef,
    cardWrapperWidth,
    cardWrapperSideMargin,
    cardContentRef,
    cardContentSidePadding,
    isConfirmButtonLoading,
    isConfirmButtonDisabled,
    isCancelButtonDisabled,
    handleSubmitForm,
    handleClickCancelButton,
    handleSuccessGoogleLogin,
    handleErrorGoogleLogin,
  } = useAccountVerificationCard({ onSuccess })

  const { width: windowWidth } = useResizeWindow()

  return (
    <Card ref={cardWrapperRef} className={cn('max-w-[420px]', 'mx-5')}>
      <CardHeader>
        <CardTitle>Verify Yourself</CardTitle>
      </CardHeader>
      <CardContent ref={cardContentRef}>
        <p className={cn('mb-6', 'text-sm font-normal text-muted-foreground')}>
          Account verification is required to delete account. If you are signed in with Google, please proceed with
          authentication using Google Sign-in, or confirm with password.
        </p>

        <div className={cn('flex justify-center')}>
          {/* Make Google login widget responsive */}
          <GoogleLogin
            width={
              windowWidth < cardWrapperWidth + cardWrapperSideMargin * 2
                ? windowWidth - cardContentSidePadding * 2 - cardWrapperSideMargin * 2
                : 360
            }
            text="continue_with"
            onSuccess={handleSuccessGoogleLogin}
            onError={handleErrorGoogleLogin}
          />
        </div>

        <Separator orientation="horizontal" className={cn('relative', 'my-6')}>
          <span
            className={cn(
              'absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
              'text-xs text-muted-foreground',
              'bg-white',
              'px-2'
            )}
          >
            OR VERIFY WITH
          </span>
        </Separator>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmitForm)}>
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <PasswordInput {...field} />
                  {formErrors.currentPassword && (
                    <p className={cn('text-sm font-normal text-rose-600', '!mt-1')}>
                      {formErrors.currentPassword?.message}
                    </p>
                  )}
                </FormItem>
              )}
            />

            <div className={cn('flex gap-x-3', 'mt-6')}>
              <Button
                type="submit"
                variant="default"
                disabled={isConfirmButtonDisabled}
                loading={isConfirmButtonLoading}
                className={cn('w-2/3')}
              >
                Confirm
              </Button>
              <Button
                type="button"
                variant="secondary"
                className={cn('w-1/3')}
                disabled={isCancelButtonDisabled}
                onClick={handleClickCancelButton}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
